import type { ReactNode } from 'react';

import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  SxProps,
  Theme,
  styled,
} from '@mui/material';

const TableContainerStyled = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: theme.backrs.radius['radius-md'],
}));

export type GenericTableProps = Readonly<{
  headers: readonly ReactNode[];
  children: React.ReactNode;
  ariaLabel?: string;
  stickyHeader?: boolean;
}>;

export const GenericTable = ({
  headers,
  children,
  ariaLabel,
  stickyHeader = false,
}: GenericTableProps) => {
  return (
    <TableContainer component={TableContainerStyled}>
      <Table size="small" aria-label={ariaLabel} stickyHeader={stickyHeader}>
        <TableHead>
          <TableRow>
            {headers.map((header, idx) => (
              <TableCell key={idx} sx={tableHeaderLabelSx}>
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody sx={tableBodySx}>{children}</TableBody>
      </Table>
    </TableContainer>
  );
};

const tableHeaderLabelSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-xs-semibold'],
  color: backrs.colors['Colors/Text/text-quaternary'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  paddingBlock: backrs.spacing['spacing-lg'],
  '&:first-of-type': {
    paddingLeft: backrs.spacing['spacing-3xl'],
  },
  '&:last-of-type': {
    paddingRight: backrs.spacing['spacing-3xl'],
  },
});

const tableBodySx: SxProps<Theme> = ({ backrs }) => ({
  '& tr:last-of-type td': {
    border: 0,
  },
  '& td:first-of-type': {
    paddingLeft: backrs.spacing['spacing-3xl'],
  },
  '& td:last-of-type': {
    paddingRight: backrs.spacing['spacing-3xl'],
  },
});
